import { useState } from "react";
import type { EscapeExperience } from "./EscapeExperienceWizard";

export type AgendaSlot = { date: string; time: string; dayOffset: number; lateNight: boolean; available: boolean; reason: string | null; teamsLeft?: number };
export type AgendaBooking = {
  id: string; experienceName: string; resourceKey: string; date: string; serviceDate: string; time: string; partySize: number;
  status: "pending" | "confirmed" | "cancelled"; source: string; notes: string; createdAt: string;
  customer: { firstName: string; lastName: string; email: string; phone: string };
};

function addDays(day: string, delta: number) {
  const value = new Date(`${day}T12:00:00`);
  value.setDate(value.getDate() + delta);
  return `${value.getFullYear()}-${String(value.getMonth() + 1).padStart(2, "0")}-${String(value.getDate()).padStart(2, "0")}`;
}

function nightLabel(day: string) {
  const current = new Date(`${day}T12:00:00`);
  const previous = new Date(current); previous.setDate(previous.getDate() - 1);
  const weekday = (value: Date) => value.toLocaleDateString("it-IT", { weekday: "long" });
  return `notte tra ${weekday(previous)} e ${weekday(current)}`;
}

const STATUS_LABEL = { pending: "Da confermare", confirmed: "Confermata", cancelled: "Annullata" } as const;

export default function EscapeAgenda({ experience, sharedWith, slots, bookings, date, onDate, canEdit, busyKey, onClose, onBooking, onEditCard }: {
  experience: EscapeExperience;
  sharedWith: string[];
  slots: AgendaSlot[];
  bookings: AgendaBooking[];
  date: string;
  onDate: (value: string) => void;
  canEdit: boolean;
  busyKey: string;
  onClose: (resourceKey: string, date: string, time: string, closed: boolean) => void;
  onBooking: (booking: AgendaBooking, status: AgendaBooking["status"]) => void;
  onEditCard: () => void;
}) {
  const day = new Date(`${date}T12:00:00`).toLocaleDateString("it-IT", { weekday: "long", day: "numeric", month: "long", year: "numeric" });
  const capacity = experience.editorial.slotCapacity ?? 1;
  const online = experience.active && experience.editorial.visibility === "available" && experience.bookingMode === "schedule";
  const open = bookings.filter((booking) => booking.status !== "cancelled");
  // Le annullate restano consultabili ma nascoste di default: la lista mostra prima chi verrà davvero.
  const [showCancelled, setShowCancelled] = useState(() => {
    try { return localStorage.getItem("ub_show_cancelled") === "1"; } catch { return false; }
  });
  const toggleCancelled = () => setShowCancelled((current) => {
    try { localStorage.setItem("ub_show_cancelled", current ? "0" : "1"); } catch { /* preferenza solo locale */ }
    return !current;
  });
  const cancelledCount = bookings.length - open.length;
  const visibleBookings = showCancelled ? bookings : open;

  return <div className="agenda">
    <div className="agenda-date">
      <button type="button" onClick={() => onDate(addDays(date, -1))} aria-label="Giorno precedente">‹</button>
      <label><span>Giorno da gestire</span><input type="date" value={date} onChange={(event) => onDate(event.target.value)} /></label>
      <button type="button" onClick={() => onDate(addDays(date, 1))} aria-label="Giorno successivo">›</button>
      <strong>{day}</strong>
    </div>

    <h4>Turni di «{experience.name}»</h4>
    {!online && <div className="agenda-empty">
      {experience.bookingMode === "inquiry"
        ? "Questa esperienza funziona con richieste su WhatsApp: non ha turni da gestire."
        : "Questa esperienza non è ancora online con le prenotazioni, quindi non ha turni prenotabili."}
      {" "}<button type="button" className="wiz-link" onClick={onEditCard}>Modifica la scheda →</button>
    </div>}
    {online && slots.length === 0 && <div className="agenda-empty">Nessun turno in questo giorno.</div>}
    {online && slots.length > 0 && <div className="agenda-slots">{slots.map((slot) => {
      const key = `${experience.resourceKey}-${slot.date}-${slot.time}`;
      const state = slot.available ? (capacity > 1 ? `${slot.teamsLeft ?? capacity} posti squadra liberi` : "Libero") : slot.reason === "reserved" ? "Pieno" : slot.reason === "past" ? "Trascorso" : slot.reason === "call" ? "Troppo vicino: solo telefono" : "Chiuso";
      return <div className={`agenda-slot ${slot.available ? "is-open" : "is-closed"}`} key={key}>
        <div><strong>{slot.time}</strong>{slot.lateNight && <small>{nightLabel(slot.date)}</small>}</div>
        <span>{state}</span>
        {slot.reason !== "reserved" && slot.reason !== "past" && <button type="button" disabled={!canEdit || busyKey === key} onClick={() => onClose(experience.resourceKey, slot.date, slot.time, slot.available)}>{slot.available ? "Chiudi questo turno" : "Riapri"}</button>}
      </div>;
    })}</div>}
    {sharedWith.length > 0 && <p className="agenda-note">Stesso luogo di <strong>{sharedWith.join(", ")}</strong>: se chiudi o riempi un turno qui, non è più disponibile neppure per loro alla stessa ora.</p>}

    <h4>Richieste per questa serata {open.length > 0 && <span className="agenda-count">{open.length}</span>}</h4>
    {bookings.length > 0 && <div className="agenda-booking-toolbar">
      <p className="status-legend"><span><i className="is-confirmed" /> Confermata</span><span><i className="is-pending" /> Da confermare</span><span><i className="is-cancelled" /> Annullata</span></p>
      {cancelledCount > 0 && <button type="button" className="show-cancelled-toggle" aria-pressed={showCancelled} onClick={toggleCancelled}>{showCancelled ? "Nascondi annullate" : `Mostra annullate (${cancelledCount})`}</button>}
    </div>}
    {bookings.length === 0 ? <div className="agenda-empty">Nessuna richiesta per «{experience.name}» in questa serata.</div> : visibleBookings.length === 0 ? <div className="agenda-empty">Solo richieste annullate in questa serata.</div> : visibleBookings.map((booking) => <article className={`agenda-booking is-${booking.status}`} key={booking.id}>
      <div>
        <small>{booking.time}{booking.serviceDate !== booking.date ? ` · ${nightLabel(booking.date)}` : ""} · {booking.partySize} persone</small>
        <h5>{booking.customer.firstName} {booking.customer.lastName}</h5>
        <p><a href={`tel:${booking.customer.phone}`}>{booking.customer.phone}</a>{booking.customer.email && <> · <a href={`mailto:${booking.customer.email}`}>{booking.customer.email}</a></>}</p>
        {booking.notes && <p className="agenda-notes">«{booking.notes}»</p>}
        <small>Ricevuta il {new Date(booking.createdAt).toLocaleString("it-IT", { timeZone: "Europe/Rome" })}</small>
      </div>
      <div className="agenda-actions">
        <span className={`agenda-badge is-${booking.status}`}>{STATUS_LABEL[booking.status]}</span>
        {booking.status !== "confirmed" && <button type="button" className="wiz-primary" disabled={!canEdit || busyKey === booking.id} onClick={() => onBooking(booking, "confirmed")}>Conferma</button>}
        {booking.status !== "cancelled" && <button type="button" disabled={!canEdit || busyKey === booking.id} onClick={() => onBooking(booking, "cancelled")}>Annulla</button>}
        {booking.status === "cancelled" && <button type="button" disabled={!canEdit || busyKey === booking.id} onClick={() => onBooking(booking, "pending")}>Rimetti da confermare</button>}
      </div>
    </article>)}
  </div>;
}
